/**
 * AutoAcceptPanel — Supplier-side Auto-Accept control.
 *
 * Trust + Control + Safety. If a supplier feels even slightly unsafe,
 * they won't enable it — so the copy explicitly surfaces the guardrails
 * ("Only accepts when your calendar is free", "You stay in control",
 * "Never double-booked") before the enable button.
 *
 * Studio Pass gated — free suppliers see an upgrade card instead.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lightning, CheckCircle, Lock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOTICE_OPTS = [6, 12, 24, 48, 72, 168];
const DURATION_OPTS = [2, 4, 6, 8, 12];

const AutoAcceptPanel = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    axios.get(`${API}/suppliers/me/auto-accept`)
      .then((r) => { setCfg(r.data); setDraft(r.data); })
      .catch(() => {});
  }, []);

  const save = async (override = null) => {
    if (saving) return;
    setSaving(true);
    const payload = override || draft;
    try {
      const { data } = await axios.put(`${API}/suppliers/me/auto-accept`, payload);
      setCfg(data);
      setDraft(data);
      setShowSetup(false);
      toast.success(data.enabled ? 'Auto-Accept is ON ⚡' : 'Auto-Accept turned off');
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'upgrade_required') {
        toast.error('Upgrade to Studio Pass to enable Auto-Accept');
        navigate('/studio-pass');
      } else {
        toast.error(detail?.message || detail || 'Could not save');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  // Free tier — sell outcome not feature
  if (!cfg.is_pro) {
    return (
      <div
        className="rounded-2xl p-4 mb-5 border border-white/10"
        style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.18), rgba(194,157,82,0.08))' }}
        data-testid="auto-accept-upgrade"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Lock size={14} weight="fill" className="text-[#C29D52]" />
          <p className="text-[10px] font-black uppercase tracking-[2px] text-[#C29D52]/85">Studio Pass feature</p>
        </div>
        <h3 className="text-base font-black mb-1">Get booked without lifting a finger ⚡</h3>
        <p className="text-white/60 text-xs mb-3 leading-relaxed">
          Auto-Accept confirms bookings instantly while you're busy. You stay in control — never double-booked.
        </p>
        <button
          onClick={() => navigate('/studio-pass')}
          className="px-4 py-2 rounded-lg text-xs font-bold text-white"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="auto-accept-upgrade-cta"
        >
          Upgrade
        </button>
      </div>
    );
  }

  // Pro, enabled — confidence signal
  if (cfg.enabled) {
    return (
      <div
        className="rounded-2xl p-4 mb-5 border border-emerald-400/30 flex items-center justify-between gap-3"
        style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(194,157,82,0.06))' }}
        data-testid="auto-accept-on"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-emerald-400/20 flex items-center justify-center shrink-0">
            <CheckCircle size={18} weight="fill" className="text-emerald-300" />
          </div>
          <div className="min-w-0">
            <p className="font-black text-sm">Auto-Accept: <span className="text-emerald-300">ON ⚡</span></p>
            <p className="text-white/55 text-[11px] leading-tight">
              Automatically accepting bookings · {cfg.min_notice_hours}h notice · up to {cfg.max_duration_hours}h
            </p>
          </div>
        </div>
        <button
          onClick={() => save({ ...cfg, enabled: false })}
          disabled={saving}
          className="shrink-0 px-3 py-2 rounded-lg text-xs font-bold bg-white/10 hover:bg-white/15 border border-white/10"
          data-testid="auto-accept-off-btn"
        >
          Turn off
        </button>
      </div>
    );
  }

  // Pro, disabled — entry point
  if (!showSetup) {
    return (
      <div
        className="rounded-2xl p-4 mb-5 border border-white/10 flex items-center justify-between gap-3"
        style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.10), rgba(194,157,82,0.04))' }}
        data-testid="auto-accept-entry"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Lightning size={20} weight="fill" className="text-[#C29D52] shrink-0" />
          <div className="min-w-0">
            <p className="font-black text-sm leading-tight">Get booked automatically ⚡</p>
            <p className="text-white/50 text-[11px] leading-tight mt-0.5">
              Auto-confirm bookings that fit your schedule.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowSetup(true)}
          className="shrink-0 px-4 py-2 rounded-lg text-xs font-bold text-white"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="auto-accept-enable-btn"
        >
          Enable
        </button>
      </div>
    );
  }

  // Setup flow — trust-building copy before the enable action
  return (
    <div
      className="rounded-2xl p-5 mb-5 border border-white/10 bg-white/[0.03]"
      data-testid="auto-accept-setup"
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        >
          <Lightning size={18} weight="fill" className="text-white" />
        </div>
        <div>
          <h3 className="text-base font-black leading-tight">Get booked automatically ⚡</h3>
          <p className="text-white/55 text-xs mt-0.5">
            Automatically accept booking requests when you're available
          </p>
        </div>
      </div>

      <ul className="space-y-1.5 mb-4 text-xs text-white/75">
        <li className="flex items-center gap-2">
          <span className="text-emerald-300">✔</span> Only accepts when your calendar is free
        </li>
        <li className="flex items-center gap-2">
          <span className="text-emerald-300">✔</span> You stay in control — turn off anytime
        </li>
        <li className="flex items-center gap-2">
          <span className="text-emerald-300">✔</span> Never double-booked
        </li>
      </ul>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-[1.5px] text-white/40 mb-1.5">
            Minimum notice
          </label>
          <select
            value={draft.min_notice_hours}
            onChange={(e) => setDraft({ ...draft, min_notice_hours: Number(e.target.value) })}
            className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-2 py-2 text-xs text-white"
            data-testid="min-notice-select"
          >
            {NOTICE_OPTS.map((h) => (
              <option key={h} value={h} className="bg-[#0F1014]">
                {h < 24 ? `${h} hours` : `${Math.round(h / 24)} day${h === 24 ? '' : 's'}`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-[1.5px] text-white/40 mb-1.5">
            Max duration
          </label>
          <select
            value={draft.max_duration_hours}
            onChange={(e) => setDraft({ ...draft, max_duration_hours: Number(e.target.value) })}
            className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-2 py-2 text-xs text-white"
            data-testid="max-duration-select"
          >
            {DURATION_OPTS.map((h) => (
              <option key={h} value={h} className="bg-[#0F1014]">{h} hours</option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-[11px] text-white/45 mb-4">
        Only accepts bookings that fit your schedule. You stay in control.
      </p>

      <div className="flex gap-2">
        <button
          onClick={() => save({ ...draft, enabled: true })}
          disabled={saving}
          className="flex-1 py-2.5 rounded-lg text-xs font-black text-white disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="auto-accept-save-btn"
        >
          {saving ? 'Saving…' : 'Save & Enable'}
        </button>
        <button
          onClick={() => { setDraft(cfg); setShowSetup(false); }}
          className="px-4 py-2.5 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AutoAcceptPanel;
